import React, { useCallback, useRef, useEffect, useState } from 'react';
import { checkExistsDiacriticSpecialCharacters, regexValidation } from '../../../utils/cob-utils';
import { IInputTextProps } from '../../../component-library/atoms/InputText/InputText';
import { InputText } from 'component-library/index';
import InputTextOrangeVariant from '../../../component-library/atoms/InputText/Variants/OrangeAsterisk/InputTextOrangeVariant';
import { generateFieldErrorMessage } from '../../../utils/error-message';

export interface INameFieldProps {
    name: 'Last Name' | 'First Name';
    label?: string | React.ReactElement<any, string | React.JSXElementConstructor<any>> | React.ReactElement<any, string | React.JSXElementConstructor<any>>[];
    inputClass?: string;
    value?: string;
    setValueCallback?: THandlerFunction;
    error?: boolean;
    invalidErrorMessage?: string;
    requiredMessage?: string;
    alternate?: boolean;
    onFocusCallback?: (((value: React.FocusEvent<HTMLInputElement>) => void) & React.FocusEventHandler<any>);
    onBlurCallback?: THandlerFunction | (() => Promise<void>);
    disabled?: boolean;
    ref?: React.MutableRefObject<any>;
    valid?: [boolean, (arg?: any) => void];
    // DPM-7649 revisit to fix styling differences between PersonalInformation.tsx and PersonalInformationForm.tsx
    // and prefer a single method/strategy for green checkmarks.
    //rest parameter for now, until inputs clean up have finished.
    useSuccessIcon?: boolean;
    showSuccessIcon?: boolean;
    removeAsterisk?: boolean;
    isAU?: boolean;
    isAUInEditMode?: boolean;
    orangeAsterisksOnMobile?: boolean;
}

export default function NameField(props: INameFieldProps & React.HTMLAttributes<HTMLInputElement>, ...rest) {
    const inputBaseRef = useRef<HTMLInputElement|null>(props.ref?.current ?? null);
    const [state, setState] = useState<IInputTextProps['state']>('neutral');    
    const [isBlurred, setIsBlurred] = useState(null);
    const regex = regexValidation();
    const fieldType = props.name === 'Last Name' ? 'last' : 'first';
    const fieldMessage = generateFieldErrorMessage(props.name, props.value === '' ? 'missingValue' : null, null);

    const validateFirstAndLastName = useCallback((toValidate: any) => {
        toValidate = toValidate ? toValidate.trim() : '';
        return !!(toValidate && 
            regex.allow[`${fieldType}_name`].test(toValidate) && 
            !regex.disallow[`${fieldType}_name`].test(toValidate) &&
            !checkExistsDiacriticSpecialCharacters(toValidate));
    }, [
        props.name,
    ]);

    const formattedFirstAndLastName = (nextValue: string) => {
        if (nextValue?.length > 60) {
            nextValue = nextValue.substring(0, 60);
        }

        if (nextValue) {
            let format = '';
            const isAposthropheRepeated = nextValue.match(/'/g)?.length === 2 && nextValue.substring(nextValue.length - 1) === '\'';
    
            if (isAposthropheRepeated) {
                format = nextValue.substring(0, nextValue.length - 1);
            } else {
                if (fieldType === 'last') {
                    format = nextValue
                        .replace(/[^a-zA-Z-'\s]/g, '')
                        .replace(/^'/g, '')
                        .replace(/'+/g, '\'');
                } else {
                    format = nextValue
                        .replace(/[^a-zA-Z-\s]/g, '');
                }
                format = format
                    .replace(/^-/g, '')
                    .replace(/-{2,}/g, '-')
                    .replace(/\s{2,}/g, ' ');
            }
            return format;
        }
    };

    const valueHandler = (nextValue: string) => {
        props.setValueCallback && props.setValueCallback(formattedFirstAndLastName(nextValue));
    };

    const focusHandler = (event: React.FocusEvent<HTMLInputElement>) => {
        setIsBlurred(false);

        if (state === 'success') {
            setState('neutral');
        }

        props.onFocusCallback && props.onFocusCallback(event);
    };

    const blurHandler = () => {
        setIsBlurred(true);

        if (props.error) {
            setState('error');
            return;
        }

        const updatedValue = props?.value?.trim();

        const validation = validateFirstAndLastName(updatedValue);

        if (props.valid) {
            props.valid[1](validation);
        }
        
        props.setValueCallback && props.setValueCallback(updatedValue);

        props.onBlurCallback && props.onBlurCallback(updatedValue, validation);
        setState(validation ? 'success': 'error');
        
    };

    const changeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (props.value !== event.target.value) {
            setState('neutral');
        }

        valueHandler(event.target.value);
    };

    useEffect(() => {
        if (props.error) {
            setState('error');
            return;
        } else if (props.isAU && props.value) {
            setState(isBlurred ? 'success': 'neutral');
        }
    }, [props.error]);

    useEffect(() => {
        if(!props.isAUInEditMode) {
            return;
        }
        setState('neutral');
    }, [props.isAUInEditMode]);

    if(!props.orangeAsterisksOnMobile) {
        return (
            <>
                <InputText
                    required={true}
                    name={props.name}
                    ref={inputBaseRef}
                    label={props.label}
                    id={props.id}
                    value={props.value}
                    setValue={valueHandler}
                    onFocus={focusHandler}
                    onBlur={blurHandler}
                    onChange={changeHandler}
                    disabled={props.disabled}        
                    errorMessage={fieldMessage}
                    state={state}
                    maxLength={30}
                    {...rest}    
                />
            </>
        );
    }

    return (
    <>
        <InputTextOrangeVariant
            required={true}
            name={props.name}
            ref={inputBaseRef}
            label={props.label}
            id={props.id}
            value={props.value}
            setValue={valueHandler}
            onFocus={focusHandler}
            onBlur={blurHandler}
            onChange={changeHandler}
            disabled={props.disabled}        
            errorMessage={fieldMessage}
            state={state}
            maxLength={30}
            {...rest}    
        />
    </>
);
}